import { useCallback, useMemo } from 'react';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import {
  NotificationTypes,
  notificationTypesReadable,
} from '@dynamika-shared/types/subscription';
import { StateFilterItem, AllFilters } from '@components/NestedFilter/types';
import { useClinicalTrialNotificationHistorySlice } from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.slice';
import {
  PossibleNotificationHistoryFilters,
} from '@restore/clinicalTrialNotificationHistory/notificationHistoryFilters.types';
import { NotificationDeliveryStatus } from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.types';
import { STATUS_LABEL } from '../constants';

const FILTER_GROUP_KEY = 'notification-history-filters-group';

const ALL_FILTER_OPTIONS: AllFilters<
  PossibleNotificationHistoryFilters,
  string
> = {
  notificationType: Object.values(NotificationTypes).map(type => ({
    value: type,
    label: notificationTypesReadable[type],
  })),
  status: Object.values(NotificationDeliveryStatus).map(status => ({
    value: status,
    label: STATUS_LABEL[status],
  })),
};

const isPossibleNotificationHistoryFilter = (
  key: string
): key is PossibleNotificationHistoryFilters =>
  key === 'notificationType' || key === 'status' || key === 'recipient';

export const useNotificationHistoryFilters = () => {
  const {
    filterState,
    selectedFilters,
    toggleActiveFilters,
    toggleSelectedFilters,
    setSelectedFilter,
    resetFilters,
  } = useClinicalTrialNotificationHistorySlice((state, actions) => ({
    filterState: state.filters.state,
    selectedFilters: state.filters.selectedFilters,
    toggleActiveFilters: actions.toggleActiveFilters,
    toggleSelectedFilters: actions.toggleSelectedFilters,
    setSelectedFilter: actions.setSelectedFilter,
    resetFilters: actions.resetFilters,
  }));

  // The option set is static (enums), so we don't need to populate the slice.
  const allFilters = ALL_FILTER_OPTIONS;

  const stateEntries = useMemo(
    () =>
      Object.entries(filterState) as [
        PossibleNotificationHistoryFilters,
        StateFilterItem,
      ][],
    [filterState]
  );

  const filters = useMemo(
    () => [
      {
        key: FILTER_GROUP_KEY,
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: stateEntries.map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={filter.active} />
            </div>
          ),
        })),
      },
    ],
    [stateEntries]
  );

  const toggleActiveFilter = useCallback(
    (name: PossibleNotificationHistoryFilters) => {
      toggleActiveFilters(name);
    },
    [toggleActiveFilters]
  );

  const toggleSelectedFilter = useCallback(
    ({
      name,
      value,
    }: {
      name: PossibleNotificationHistoryFilters;
      value: string;
    }) => {
      toggleSelectedFilters({ name, value });
    },
    [toggleSelectedFilters]
  );

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      const key = String(item.key);
      if (isPossibleNotificationHistoryFilter(key)) {
        toggleActiveFilter(key);
      }
    },
    [toggleActiveFilter]
  );

  const reset = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const setRecipient = useCallback(
    (value: string | undefined) => {
      setSelectedFilter({ name: 'recipient', value });
    },
    [setSelectedFilter]
  );

  const recipient = selectedFilters.recipient?.[0];

  return {
    subFilters: stateEntries,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    reset,
    recipient,
    setRecipient,
  };
};
